import { ChevronDown } from 'lucide-react';
import React, { useState } from 'react';

interface Rechazo {
  fecha: string;
  contenido: string;
}

function formatFechaTextual(fechaRaw: string): string {
  // Formato esperado: "8/04/26, 7:25:10 a. m."
  // Resultado esperado: "8 de abril de 2026 a las 7:25 a. m."
  try {
    const match = fechaRaw.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4}),\s*(.+)/);
    if (!match) return fechaRaw;

    const [, dia, mes, anio, hora] = match;
    const meses = [
      'enero',
      'febrero',
      'marzo',
      'abril',
      'mayo',
      'junio',
      'julio',
      'agosto',
      'septiembre',
      'octubre',
      'noviembre',
      'diciembre',
    ];

    const mesNum = parseInt(mes, 10) - 1;
    const mesNombre = meses[mesNum] || mes;
    const anioCompleto = anio.length === 2 ? `20${anio}` : anio;
    const horaLimpia = hora.trim();

    return `${dia} de ${mesNombre} de ${anioCompleto} a las ${horaLimpia}`;
  } catch (e) {
    return fechaRaw;
  }
}

export function extractRechazos(text: string): Rechazo[] {
  if (!text) return [];

  const sections = text.split(/(\[\d{1,2}\/\d{1,2}\/\d{2,4},.*?\] RECHAZO ADMIN:)/g);
  const rechazos: Rechazo[] = [];

  for (let i = 0; i < sections.length; i += 2) {
    const header = sections[i + 1];
    const content = sections[i + 2];

    if (!header || !content) continue;

    const fechaMatch = header.match(/\[(.+?)\] RECHAZO ADMIN:/);
    const fechaRaw = fechaMatch ? fechaMatch[1] : 'Fecha no disponible';

    rechazos.push({
      fecha: fechaRaw,
      contenido: content.trim(),
    });
  }

  return rechazos.reverse(); // Invertir para mostrar el más reciente primero
}

// Compacto: solo el último rechazo para EmpresaDetallePage
export function renderRecomendacionesCompacto(text: string) {
  if (!text) return null;

  const rechazos = extractRechazos(text);
  if (rechazos.length === 0) {
    return <span className="whitespace-pre-wrap text-gray-700 text-sm">{text}</span>;
  }

  const ultimoRechazo = rechazos[0];

  return (
    <div className="bg-red-50 border border-red-200 rounded-lg overflow-hidden">
      <div className="bg-red-100 px-3 py-1.5 flex items-center justify-between">
        <p className="text-[10px] font-bold text-red-600 uppercase tracking-tight">Rechazo más reciente</p>
        <p className="text-[9px] text-red-600 italic">{formatFechaTextual(ultimoRechazo.fecha)}</p>
      </div>
      <div className="p-3">
        <p className="text-sm text-gray-700 whitespace-pre-wrap line-clamp-2">{ultimoRechazo.contenido}</p>
        {rechazos.length > 1 && <p className="text-[10px] text-gray-500 mt-1 italic">+{rechazos.length - 1} rechazo(s) anterior(es)</p>}
      </div>
    </div>
  );
}

// Expandible: todos los rechazos para AsesoriaDetallePage
export function renderRecomendacionesExpandible(text: string) {
  if (!text) return null;

  const rechazos = extractRechazos(text);
  if (rechazos.length === 0) {
    return <span className="whitespace-pre-wrap text-gray-700 text-sm">{text}</span>;
  }

  return <RechazosExpandibles rechazos={rechazos} />;
}

function RechazosExpandibles({ rechazos }: { rechazos: Rechazo[] }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="space-y-2">
      {/* Mostrar siempre el último (más reciente) */}
      <div className="bg-red-50 border border-red-200 rounded-lg overflow-hidden">
        <div className="bg-red-100 px-3 py-1.5 flex items-center justify-between">
          <p className="text-[10px] font-bold text-red-600 uppercase tracking-tight">Rechazo más reciente</p>
          <p className="text-[9px] text-red-600 italic">{formatFechaTextual(rechazos[0].fecha)}</p>
        </div>
        <div className="p-3">
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{rechazos[0].contenido}</p>
        </div>
      </div>

      {/* Mostrar botón si hay más rechazos */}
      {rechazos.length > 1 && (
        <>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-[11px] font-bold text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors uppercase tracking-tight"
          >
            <ChevronDown className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            Ver {rechazos.length - 1} rechazo(s) anterior(es)
          </button>

          {isExpanded && (
            <div className="space-y-2 mt-2 pt-2 border-t border-gray-200">
              {rechazos.slice(1).map((rechazo, index) => (
                <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-tight mb-1">{formatFechaTextual(rechazo.fecha)}</p>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{rechazo.contenido}</p>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// helper to render **bold** markup and preserve line breaks
export function renderWithBold(text: string) {
  // split by segments that have exactly two newlines before and after **bold**
  const parts = text.split(/(\n\n\*\*[\s\S]+?\*\*\n\n)/g);
  return parts.map((p, i) => {
    if (/^\n\n\*\*[\s\S]+?\*\*\n\n$/.test(p)) {
      // strip the two leading newlines and two trailing newlines, then remove the ** markers
      // we still want to leave a single newline before and after the bolded text when rendering
      let inner = p.slice(2, -2).slice(2, -2);
      // if the bold text itself ended with an extra blank line, drop only one newline
      if (inner.endsWith('\n\n')) {
        inner = inner.slice(0, -1);
      }
      return (
        <React.Fragment key={i}>
          {/* one newline before bold */}
          <br />
          <br />
          <strong>{inner}</strong>
          {/* preserve a newline after bold instead of removing both */}
          <br />
        </React.Fragment>
      );
    }
    const lines = p.split('\n');
    return lines.map((line, idx) => (
      <React.Fragment key={`${i}-${idx}`}>
        {line}
        {idx < lines.length - 1 && <br />}
      </React.Fragment>
    ));
  });
}
